import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { legalDocumentsApi, authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import type {
    ActiveRoleAssignmentResponse,
    LegalDocumentListItem,
} from '@pillar-portal/api-types';
import { hasInternalAdminPrivileges } from '@pillar-portal/api-types';

export function LegalDocumentsListPage() {
    const log = createComponentLogger('LegalDocumentsListPage');
    const navigate = useNavigate();

    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });

    const canView: boolean = hasInternalAdminPrivileges(activeRoleAssignment ?? null);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => ['legalDocuments', `canCreate=${canView}`],
        [canView]
    );

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <InfiniteScrollList<LegalDocumentListItem, string, ReadonlyArray<string>>
                pageTitle="Legal Documents"
                queryPlaceholder="Search by title or version"
                queryKeyPrefix={queryKeyPrefix}
                pageSize={25}
                fetchPage={async (params) => {
                    const response = await legalDocumentsApi.searchLegalDocumentsPage({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                    return { items: response.legal_documents, pagination: response.pagination };
                }}
                parseCursorParam={(pageParam: unknown) => {
                    if (pageParam === null || pageParam === undefined) {
                        return undefined;
                    }
                    const parseResult = z.string().safeParse(pageParam);
                    if (!parseResult.success) {
                        throw new Error('Expected string cursor');
                    }
                    return parseResult.data;
                }}
                stateCopy={{
                    loadingLabel: 'Loading legal documents...',
                    emptyLabel: 'No legal documents found.',
                    endOfListLabel: 'End of legal documents.',
                    genericErrorLabel: 'Failed to load legal documents.',
                }}
                headerActions={
                    canView ? (
                        <Button
                            onClick={() => {
                                runInBackground(
                                    navigate('/legal-documents/create'),
                                    (err: unknown) => {
                                        log.error('Navigation failed', err);
                                    }
                                );
                            }}
                        >
                            Create
                        </Button>
                    ) : undefined
                }
                renderItems={({ items }) => (
                    <div className="space-y-3">
                        {items.map((doc: LegalDocumentListItem) => {
                            return (
                                <button
                                    key={doc.legal_document_uuid}
                                    type="button"
                                    className="w-full cursor-pointer rounded-lg border bg-card text-left hover:bg-gray-50"
                                    onClick={() => {
                                        runInBackground(
                                            navigate(`/legal-documents/${doc.legal_document_uuid}`),
                                            (err: unknown) => {
                                                log.error('Navigation failed', err);
                                            }
                                        );
                                    }}
                                >
                                    <div className="border-b p-6 pb-2">
                                        <div className="text-base font-semibold">
                                            {doc.legal_document_title} ({doc.legal_document_version})
                                        </div>
                                    </div>
                                    <div className="p-6 pt-4 text-sm text-gray-600">
                                        <div>
                                            In force: {new Date(doc.in_force_since).toLocaleString()} -{' '}
                                            {doc.in_force_to === null
                                                ? 'No end date'
                                                : new Date(doc.in_force_to).toLocaleString()}
                                        </div>
                                        <div>
                                            Required: users={String(doc.required_for_users)} orgs=
                                            {String(doc.required_for_orgs)}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            />
        </div>
    );
}
